import moment from 'moment';
import { createAuth0Client } from '@auth0/auth0-spa-js';

import {
  AUTH0_AUDIENCE,
  AUTH0_CLIENT_ID,
  AUTH0_DOMAIN,
  ClinicalRelationships,
} from '../../../../../../constants';
import { MILLISECONDS_PER_SECOND } from '../../constants';

import type { IBTSession, IProviderAssignment, ISelectOption } from '../../../../../../utils/types';
import type { IGuardianVerificationState } from '../../../types';

interface CreateGuardianVerificationStateParams {
  token: string;
  email: string;
  tokenLifespanMs: number;
  nowMs?: number;
}

interface ScheduleGuardianVerificationExpiryParams {
  currentTimeoutId: number | null;
  tokenLifespanMs: number;
  onExpire: () => void;
}

export function createGuardianVerificationState({
  token,
  email,
  tokenLifespanMs,
  nowMs = Date.now(),
}: CreateGuardianVerificationStateParams): IGuardianVerificationState {
  return {
    token,
    email,
    expiresAt: nowMs + tokenLifespanMs,
  };
}

export function scheduleGuardianVerificationExpiry({
  currentTimeoutId,
  tokenLifespanMs,
  onExpire,
}: ScheduleGuardianVerificationExpiryParams): number {
  if (currentTimeoutId) {
    window.clearTimeout(currentTimeoutId);
  }

  return window.setTimeout(onExpire, tokenLifespanMs);
}

interface GetGuardianVerificationDurationSecondsParams {
  guardianVerificationExpiresAt: number | null;
  isGuardianIdentityVerified: boolean;
}

export function getGuardianVerificationDurationSeconds({
  guardianVerificationExpiresAt,
  isGuardianIdentityVerified,
}: GetGuardianVerificationDurationSecondsParams): number | null {
  if (!guardianVerificationExpiresAt || !isGuardianIdentityVerified) return null;

  return Math.max(
    0,
    Math.floor((guardianVerificationExpiresAt - Date.now()) / MILLISECONDS_PER_SECOND),
  );
}

export function getParentEmailOptions({
  sessionClientId,
  sessionStartDate,
  providerAssignments,
}: {
  sessionClientId?: IBTSession['client_id'];
  sessionStartDate?: IBTSession['date_start'];
  providerAssignments: IProviderAssignment[];
}): ISelectOption[] {
  if (!sessionClientId || sessionStartDate == null) return [];

  const normalizedSessionStartDate = moment(sessionStartDate);
  if (!normalizedSessionStartDate.isValid()) return [];

  const activePracticingGuardianAssignments = providerAssignments.filter((assignment) => {
    const providerAssignmentStart = assignment.date_start ? moment(assignment.date_start) : null;
    const providerAssignmentEnd = assignment.date_end ? moment(assignment.date_end) : null;

    if (
      assignment.client?.id !== sessionClientId ||
      assignment.clinical_relationship !== ClinicalRelationships.PracticingGuardian ||
      !providerAssignmentStart?.isValid()
    ) {
      return false;
    }

    const startsBeforeSession = providerAssignmentStart.isSameOrBefore(normalizedSessionStartDate);
    const noEndDate = !providerAssignmentEnd;
    const endsAfterSession = providerAssignmentEnd?.isValid()
      ? providerAssignmentEnd.isSameOrAfter(normalizedSessionStartDate)
      : false;

    return startsBeforeSession && (noEndDate || endsAfterSession);
  });

  const optionsByEmail = new Map<string, ISelectOption>();

  activePracticingGuardianAssignments.forEach((assignment) => {
    const providerEmail = assignment.provider?.email;
    if (!providerEmail || optionsByEmail.has(providerEmail)) return;

    const providerFirstName = assignment.provider?.first_name?.trim() ?? '';
    const providerLastName = assignment.provider?.last_name?.trim() ?? '';
    const providerFullName = `${providerFirstName} ${providerLastName}`.trim();

    optionsByEmail.set(providerEmail, {
      value: providerEmail,
      label: providerFullName || providerEmail,
    });
  });

  return Array.from(optionsByEmail.values());
}

interface VerifyGuardianIdentityParams {
  guardianEmail: string;
}

/**
 * Uses an isolated Auth0 client in popup mode so guardian auth
 * does not interfere with the active provider session.
 */
export async function verifyGuardianIdentity({
  guardianEmail,
}: VerifyGuardianIdentityParams): Promise<string> {
  const auth0Client = await createAuth0Client({
    domain: AUTH0_DOMAIN,
    clientId: AUTH0_CLIENT_ID,
    cacheLocation: 'memory',
    useRefreshTokens: false,
    authorizationParams: {
      audience: AUTH0_AUDIENCE,
      redirect_uri: window.location.origin,
    },
  });

  await auth0Client.loginWithPopup(
    {
      authorizationParams: {
        audience: AUTH0_AUDIENCE,
        login_hint: guardianEmail,
        prompt: 'login',
        max_age: 0,
      },
    },
    {
      timeoutInSeconds: 120,
    },
  );

  return auth0Client.getTokenSilently({
    cacheMode: 'off',
    authorizationParams: {
      audience: AUTH0_AUDIENCE,
    },
  });
}
